/* eslint-disable prefer-destructuring */
/* eslint-disable import/no-extraneous-dependencies */

import { TrendDataProps, TrendProps } from "./types";
import { getCurrencySymbol, getPricingCurrency } from "./helpers";
import { monthsArray } from "@Components/DatePicker/helper";
import getUserLanguageCSR from "helpers/getUserLanguageCSR";
import config from "@Config/index";
import { formatChinaChartMonthLabel } from "@Utils/cnDisplayDate";

const userLanguage = getUserLanguageCSR();

export const lineChartLabelsEn = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const lineChartLabelsChina: any = [
  "一月", // Jan
  "二月", // Feb
  "三月", // Mar
  "四月", // Apr
  "五月", // May
  "六月", // Jun
  "七月", // Jul
  "八月", // Aug
  "九月", // Sept
  "十月", // Oct
  "十一月", // Nov
  "十二月", // Dec
] as const;

/**
 * IMPORTANT: The chart's data keys and axis categories are ALWAYS English
 * (see `lineChartLabels` / `lineChartX` below). Keeping the internal keys in a
 * single canonical language is what makes each data point map to the correct
 * axis category — mixing localized keys with localized labels caused the chart
 * to render a duplicate (empty) axis. Localization is applied at DISPLAY time
 * only, through `toDisplayMonth`, which the x-axis tick + tooltip callbacks use.
 *
 * `en` keeps its bespoke labels (it uses "Sept") and `zh` keeps its hardcoded
 * characters, so neither is generated via Intl.
 */
const INTL_MONTH_LOCALE_MAP: Record<string, string> = {
  es: "es",
  fr: "fr",
  de: "de",
};

const getLocalizedShortMonths = (locale: string): string[] =>
  Array.from({ length: 12 }, (_, monthIndex) =>
    new Intl.DateTimeFormat(locale, { month: "short" }).format(
      new Date(Date.UTC(2020, monthIndex, 1)),
    ),
  );

// Maps each canonical English short month -> its localized display name.
const buildMonthDisplayMap = (lang = "en"): Record<string, string> => {
  let localized: readonly string[] | null = null;
  if (lang === "zh") {
    localized = lineChartLabelsChina;
  } else if (INTL_MONTH_LOCALE_MAP[lang]) {
    localized = getLocalizedShortMonths(INTL_MONTH_LOCALE_MAP[lang]);
  }
  if (!localized) return {};
  return lineChartLabelsEn.reduce<Record<string, string>>((acc, englishLabel, index) => {
    acc[englishLabel] = (localized as readonly string[])[index];
    return acc;
  }, {});
};

const monthDisplayMap = buildMonthDisplayMap(userLanguage);

// Translates a canonical English short-month label to the user's language for display only.
export const toDisplayMonth = (englishLabel: string): string =>
  monthDisplayMap[englishLabel] ?? englishLabel;

// English (canonical) — used as the data-point keys and the axis category values.
export const lineChartLabels: any = lineChartLabelsEn;

export const lineChartXEn: string[] = [
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
];

export const lineChartXChina = [
  "十月", // Oct
  "十一月", // Nov
  "十二月", // Dec
  "一月", // Jan
  "二月", // Feb
  "三月", // Mar
  "四月", // Apr
  "五月", // May
  "六月", // Jun
  "七月", // Jul
  "八月", // Aug
  "九月", // Sept
] as const;

// English (canonical) axis order (Oct..Sept); display names are localized in the tick callback.
export const lineChartX: any = lineChartXEn;

// Full localized month name used in the projected-price trend card.
const getForecastMonthLabel = (lang = "en"): string => {
  const nextMonthIndex = new Date().getMonth() + 1;
  const intlLocale = INTL_MONTH_LOCALE_MAP[lang];
  if (!intlLocale) {
    return monthsArray[nextMonthIndex];
  }
  return new Intl.DateTimeFormat(intlLocale, { month: "long" }).format(
    new Date(Date.UTC(2020, nextMonthIndex, 1)),
  );
};

export const getTrendCardData = (
  currentYearData: TrendProps,
  region: any,
  pricing: any,
  id: any,
  value: number,
  t: (str: string) => string,
) => [
  {
    id: 1,
    title: t("propertypage.components.pricetrends.bigSavings"),
    valuePrefix: getCurrencySymbol(pricing?.currency),
    valueSuffix: "",
    value: +currentYearData?.yearly_savings_starting_next_month,
    subText: `${t("propertypage.components.pricetrends.saveUpTo")} ${
      getCurrencySymbol(pricing?.currency) +
      parseFloat(currentYearData?.yearly_savings_starting_next_month).toFixed(2)
    } ${t("propertypage.components.pricetrends.whenYouReserveThis")} ${
      id !== value
        ? t("propertypage.components.pricetrends.room")
        : t("propertypage.components.pricetrends.property")
    } 
    ${t("propertypage.components.pricetrends.forLongTerm")}`,
    tooltip: t("propertypage.components.pricetrends.pricePatternMayVary"),
    isCardVisible: +currentYearData?.yearly_savings_starting_next_month > 0,
  },
  {
    id: 2,
    title: t("propertypage.components.pricetrends.budgetFriendly"),
    valuePrefix: "",
    valueSuffix: "%",
    value: Math.abs(+currentYearData?.cost_difference_percent),
    subText: `${t("propertypage.components.pricetrends.thisPlaceIsPriced")} ${Math.abs(
      +parseFloat(currentYearData?.cost_difference_percent).toFixed(2),
    )}% ${t("propertypage.components.pricetrends.belowAverageCostIn")} ${region?.name}.`,
    tooltip: `${t("propertypage.components.pricetrends.averagePropertyPriceIn")} ${
      region?.name
    } ${t("propertypage.components.pricetrends.is")} ${getCurrencySymbol(pricing?.currency)}${
      pricing?.duration === "monthly"
        ? Math.abs(+parseFloat(currentYearData?.city_avg_cost).toFixed(2)) * 4
        : Math.abs(+parseFloat(currentYearData?.city_avg_cost).toFixed(2))
    }/${pricing?.duration === "monthly" ? t("month") : t("week")}.`,
    isCardVisible: +currentYearData?.cost_difference_percent < 0,
  },
  {
    id: 3,
    title: t("propertypage.components.pricetrends.projectedPrice"),
    valuePrefix: "",
    valueSuffix: "%",
    value: +currentYearData?.forecasting_percent_last_year,
    subText: `${t("propertypage.components.pricetrends.priceOfThis")} ${t(
      "propertypage.components.pricetrends.room",
    )} : ${t("propertypage.components.pricetrends.property")}} 
    ${t("propertypage.components.pricetrends.isExpectedToIncreaseBy")} ${parseFloat(
      currentYearData?.forecasting_percent_last_year,
    ).toFixed(2)}% in ${getForecastMonthLabel(userLanguage)}, ${new Date().getFullYear()}.`,
    tooltip: t("propertypage.components.pricetrends.predictionBasedOnPastTrends"),
    isCardVisible: +currentYearData?.forecasting_percent_last_year > 0,
  },
  {
    id: 4,
    title: t("propertypage.components.pricetrends.bookNow"),
    valuePrefix: "",
    valueSuffix: "%",
    value: +currentYearData?.price_increase_last_year,
    subText: `${t("propertypage.components.pricetrends.this")} ${
      id !== value
        ? t("propertypage.components.pricetrends.room")
        : t("propertypage.components.pricetrends.property")
    } ${t("propertypage.components.pricetrends.priceHasGone")} ${
      +currentYearData?.price_increase_last_year > 0
        ? t("propertypage.components.pricetrends.up")
        : t("propertypage.components.pricetrends.down")
    } by ${Math.abs(+currentYearData?.price_increase_last_year)}% ${t(
      "propertypage.components.pricetrends.inPast12Months",
    )}`,
    tooltip: t("propertypage.components.pricetrends.infoBasedOnLast12Months"),
    isCardVisible: !Number.isNaN(+currentYearData?.price_increase_last_year),
  },
];

export const getLineChartOptions = (
  currency = "",
  duration = "",
  isMobile: boolean,
  fromText = "From",
) => {
  const getCustomBorderRadius = (context: any) => {
    const index: number = context?.dataIndex;
    const currentMonth: number = new Date().getMonth();
    // adding 2 bacause x-axis ranges from oct to sept
    return index === currentMonth + 3 ? 4 : 0;
  };
  return {
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    plugins: {
      legend: {
        align: isMobile ? ("start" as const) : ("end" as const),
        position: "top" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          borderRadius: 6,
          useBorderRadius: true,
          font: {
            size: 12,
            weight: 500,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        padding: 16,
        boxWidth: 12,
        boxHeight: 12,
        enabled: true,
        borderWidth: 1,
        mode: "nearest" as const,
        axis: "x" as const,
        bodySpacing: 16,
        titleColor: "#000",
        usePointStyle: true,
        backgroundColor: "#fff",
        bodyColor: "#111928",
        borderColor: "#e5e7eb",
        bodyFont: {
          size: 12,
          weight: 500,
        },
        callbacks: {
          title: () => "",
          label: (context: any) => {
            const { label, parsed, raw } = context;
            const monthLabel = config.IS_CHINA_APP
              ? formatChinaChartMonthLabel(label, raw.year) ||
                `${toDisplayMonth(label)}’${raw.year}`
              : `${toDisplayMonth(label)}’${raw.year}`;
            const formattedLabel = `\u00A0\u00A0${monthLabel}\u00A0\u00A0\u00A0\u00A0 ${fromText}: ${getPricingCurrency(
              currency,
            )}${parsed?.y}`;
            return formattedLabel;
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    elements: {
      point: {
        display: true,
        radius: getCustomBorderRadius,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "",
          color: "#6b7280",
          font: {
            size: 10,
            weight: 400,
            family: "Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif",
          },
        },
        ticks: {
          // `value` is the category index; resolve to the English label then localize for display.
          callback(value: any) {
            const englishLabel = typeof value === "number" ? lineChartX[value] : value;
            return toDisplayMonth(englishLabel);
          },
          font: {
            family: "Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif",
          },
        },
      },
      y: {
        color: "#e5e7eb",
        ticks: {
          callback(value: any) {
            return getPricingCurrency(currency) + value;
          },
          stepSize: 100,
        },
        title: {
          display: true,
          text:
            userLanguage === "zh"
              ? `${duration[0]?.toUpperCase()}${duration?.slice(1)} 租金`
              : `${duration[0]?.toUpperCase()}${duration?.slice(1)} Rent`,
          color: "#6b7280",
          font: {
            size: 10,
            weight: 400,
            family: "Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif",
          },
        },
        beginAtZero: true,
      },
    },
  };
};

export const getLineChartData = (trendData: TrendDataProps) => {
  let currentYear = new Date().getFullYear();
  currentYear = new Date().getMonth() >= 9 ? currentYear + 1 : currentYear;

  return {
    labels: lineChartX,
    datasets: [
      {
        label: `${currentYear - 1}-${currentYear}`,
        data: trendData?.currentYear,
        borderColor: "#3f83f8",
        backgroundColor: "#3f83f8",
        borderWidth: 2,
      },
      {
        label: `${currentYear - 2}-${currentYear - 1}`,
        data: trendData?.lastYear,
        borderColor: "#9ca3af",
        backgroundColor: "#9ca3af",
        borderWidth: 2,
      },
    ],
  };
};

export const getLineChartPlugins = () => [
  {
    id: "custom_highlight_axis_line_on_hover",
    afterDraw: (chart: any) => {
      if (chart.tooltip?._active?.length) {
        const { x } = chart.tooltip._active[0].element;
        const yAxis = chart.scales.y;
        const { ctx } = chart;
        ctx.save();
        ctx.setLineDash([5, 5]); // Set a dashed line pattern with 5 pixels on and 5 pixels off
        ctx.beginPath();
        ctx.moveTo(x, yAxis.top);
        ctx.lineTo(x, yAxis.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#1f2a37";
        ctx.stroke();
        ctx.restore();
        ctx.setLineDash([]); // Reset the line pattern to solid
      }
    },
  },
  {
    id: "increase_legend_and_chart_padding",
    beforeInit(chart: any) {
      const fitValue = chart.legend.fit;
      chart.legend.fit = function fit() {
        fitValue.bind(chart.legend)();
        // eslint-disable-next-line no-return-assign
        return (this.height += 16);
      };
    },
  },
];

export const formatDataForGraph = (trends: TrendProps[] = [], duration: string) => {
  let currentYear = new Date().getFullYear();
  let currentMonth = new Date().getMonth();

  if (currentMonth >= 9) {
    currentYear += 1;
    currentMonth = 0;
  }
  // Save date of last 3 years, e.g. current Year is 2023 then yearData[0] -> 2021, yearData[1] -> 2022, yearData[3]->2023
  // Each year object will hove keys from zero-11 representing months of an year
  const yearData: { [x: number]: number }[] = [{}, {}, {}];

  for (let i = 0; i < trends.length; i += 1) {
    const { year_of_date: year, price, month_of_date: month } = trends[i];

    // e.g. currentYear=2023 -> index = 2, 2022 -> 1, 2021 -> 0
    const index = year - currentYear + 2;
    if (index >= 0 && index <= 2) {
      yearData[index][month - 1] = price;
    }
  }

  yearData.forEach((d, i) => {
    for (let j = 0; j < 12; j += 1) {
      // if data for current month is not there, set it equal to previouse month data
      if (!d[j]) {
        // If we are iterating through january(j==0) and year is current year or previous year
        // We can set data as (year-1) December data.
        if (j == 0 && i > 0) {
          d[j] = yearData[i - 1][11];
        } else {
          d[j] = d[j - 1];
        }
      }
    }
  });

  const prevSessionData = [];
  const currentSessionData = [];
  const weeksInMonth = 4;
  // prevSession starts from (currentYear-2)th year's october(9)
  // oct, nov, dec
  for (let i = 9; i < 12; i += 1) {
    prevSessionData.push({
      x: `${lineChartLabels[i]}`,
      y:
        duration === "monthly"
          ? +yearData[0][i]?.toFixed(2) * weeksInMonth
          : +yearData[0][i]?.toFixed(2),
      year: currentYear - 2,
    });
  }
  // jan -> sept
  for (let i = 0; i < 9; i += 1) {
    if (!Number.isNaN(+yearData[1][i]?.toFixed(2)))
      prevSessionData.push({
        x: `${lineChartLabels[i]}`,
        y:
          duration === "monthly"
            ? +yearData[1][i]?.toFixed(2) * weeksInMonth
            : +yearData[1][i]?.toFixed(2),
        year: currentYear - 1,
      });
  }
  // currentSession starts from (currentYear-1)th year's october(9)
  // oct, nov, dec
  for (let i = 9; i < 12; i += 1) {
    if (!Number.isNaN(+yearData[1][i]?.toFixed(2)))
      currentSessionData.push({
        x: `${lineChartLabels[i]}`,
        y:
          duration === "monthly"
            ? +yearData[1][i]?.toFixed(2) * weeksInMonth
            : +yearData[1][i]?.toFixed(2),
        year: currentYear - 1,
      });
  }
  // Jan -> current-1 month(excluding current month as current month calculation happens in next month)
  for (let i = 0; i <= currentMonth; i += 1) {
    if (!Number.isNaN(+yearData[2][i]?.toFixed(2)))
      currentSessionData.push({
        x: lineChartLabels[i],
        y:
          duration === "monthly"
            ? +yearData[2][i]?.toFixed(2) * weeksInMonth
            : +yearData[2][i]?.toFixed(2),
        year: currentYear,
      });
  }

  return { currentYear: currentSessionData, lastYear: prevSessionData };
};

export const getCurrentYearData = (
  trends: TrendProps[] = [],
  currentSessionData: any,
  prevSessionData: any,
  duration: any,
) => {
  // getting the details of last 12 months
  const currencyYearChartData = [...prevSessionData, ...currentSessionData].slice(-12);
  const averagePriceLastYear =
    currencyYearChartData.reduce((priceCount, month) => priceCount + month.y, 0) /
    currencyYearChartData.length;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const weeksInMonth = 4;
  const foundTrend = trends.find(
    (trend) => trend.year_of_date === currentYear && trend.month_of_date === currentMonth + 1,
  );
  const current_year_data: TrendProps = foundTrend
    ? { ...foundTrend }
    : {
        month_of_date: 0,
        year_of_date: 0,
        price: 0,
        cost_difference_percent: "",
        forecasting_percent_last_year: "",
        price_increase_last_year: "",
        total_savings_for_next_month: "",
        weekly_savings_next_month: "",
        yearly_savings_starting_next_month: "",
        city_avg_cost: "",
      };
  const current_year_price =
    duration === "monthly" ? current_year_data?.price * weeksInMonth : current_year_data?.price;
  current_year_data["price_increase_last_year"] = (
    ((current_year_price - averagePriceLastYear) / averagePriceLastYear) *
    100
  ).toFixed(2);

  return current_year_data;
};

export const MIN_MANDATORY_MONTHS = 6;
